import { ChevronRight } from "Shared/appIcons";
import type { Breadcrumb } from "Shared/Layouts/components/types";
import { UnstyledLink } from "Shared/Routing/UnstyledLink";
import type { ReactNode } from "react";

type Props = {
	title: string;
	subtitle?: string;
	breadcrumbs?: Breadcrumb[];
	clientSwitcher?: ReactNode;
};

/**
 * Shared inner content of a page header — breadcrumbs, page title and the
 * meta row (client switcher · subtitle). Rendered identically by both the
 * desktop `PageHeader` and the mobile `DrawerTopbar`; only their outer chrome
 * (height, breakpoints, hamburger, actions slot) differs.
 */
export const PageHeaderBody = ({
	title,
	subtitle,
	breadcrumbs,
	clientSwitcher,
}: Props) => {
	const hasBreadcrumbs = !!breadcrumbs && breadcrumbs.length > 0;
	const hasClientSwitcher = Boolean(clientSwitcher);
	const hasSubtitle = Boolean(subtitle);
	const hasMeta = hasClientSwitcher || hasSubtitle;

	return (
		<div className="flex min-w-0 flex-1 items-center">
			{hasBreadcrumbs ? (
				<nav className="mr-3 flex items-center gap-1.5">
					{breadcrumbs.flatMap((crumb, idx) => {
						const key = `${crumb.to ?? ""}::${crumb.label}`;
						const node = crumb.to ? (
							<UnstyledLink
								key={key}
								to={crumb.to}
								className="text-body text-muted-foreground hover:text-foreground no-underline"
							>
								{crumb.label}
							</UnstyledLink>
						) : (
							<span key={key} className="text-body text-muted-foreground">
								{crumb.label}
							</span>
						);
						if (idx === 0) return [node];
						return [
							<ChevronRight
								key={`sep-${key}`}
								className="text-muted-foreground h-3.5 w-3.5"
								strokeWidth={2}
							/>,
							node,
						];
					})}
				</nav>
			) : null}
			<div className="min-w-0">
				<h1 className="text-subtitle text-foreground truncate font-semibold leading-tight">
					{title}
				</h1>
				{hasMeta ? (
					<div className="text-caption text-muted-foreground mt-0.5 flex min-w-0 items-center gap-1.5 leading-tight">
						{clientSwitcher ?? null}
						{hasClientSwitcher && hasSubtitle ? (
							<span className="text-muted-foreground flex-shrink-0">·</span>
						) : null}
						{subtitle ? <span className="truncate">{subtitle}</span> : null}
					</div>
				) : null}
			</div>
		</div>
	);
};
